import { useCallback, useEffect, useRef, useState } from 'react'

export const clampProgress = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0
  }

  return Math.max(0, Math.min(100, value))
}

export const getRandomProgressTickSeconds = (randomValue = Math.random()) =>
  Math.floor(randomValue * 6) + 1

export const getDummyDelayForProgress = (progress, randomValue = Math.random()) => {
  if (progress < 70) {
    return getRandomProgressTickSeconds(randomValue) * 1000
  }

  if (progress < 90) {
    return 60 * 1000
  }

  if (progress < 99) {
    return 300 * 1000
  }

  return null
}

export const normalizeBackendCourseProgress = payload => {
  const progress = clampProgress(payload?.cardCreationProgress)

  return {
    realProgress: progress,
    isComplete: progress >= 100,
  }
}

export const mergeDisplayedProgress = ({
  previousDisplayed,
  dummyProgress,
  realProgress,
}) => {
  return Math.min(99, Math.max(
    clampProgress(previousDisplayed),
    clampProgress(realProgress),
    clampProgress(dummyProgress),
  ))
}

export const buildCompletionRampSteps = startValue => {
  let current = Math.max(0, Math.min(99, startValue || 0))
  const steps = []

  while (current < 100) {
    const remaining = 100 - current
    const delta = remaining > 28 ? 4 : remaining > 16 ? 3 : remaining > 8 ? 2 : 1
    current = Math.min(100, current + delta)
    steps.push(current)
  }

  return steps
}

const clearTimer = timerRef => {
  if (timerRef.current) {
    clearTimeout(timerRef.current)
    timerRef.current = null
  }
}

export const useBlendedCourseProgress = isActive => {
  const [displayedProgress, setDisplayedProgress] = useState(0)
  const [isCompletionSettled, setIsCompletionSettled] = useState(false)

  const displayedRef = useRef(0)
  const realRef = useRef(0)
  const dummyRef = useRef(0)
  const backendCompletedRef = useRef(false)
  const completionStartedRef = useRef(false)
  const rampTimersRef = useRef([])
  const dummyTimerRef = useRef(null)

  const setDisplayed = useCallback(nextValue => {
    const clamped = clampProgress(nextValue)
    displayedRef.current = clamped
    setDisplayedProgress(clamped)
  }, [])

  const clearRampTimers = useCallback(() => {
    rampTimersRef.current.forEach(timerId => clearTimeout(timerId))
    rampTimersRef.current = []
  }, [])

  const reset = useCallback(() => {
    clearTimer(dummyTimerRef)
    clearRampTimers()
    displayedRef.current = 0
    realRef.current = 0
    dummyRef.current = 0
    backendCompletedRef.current = false
    completionStartedRef.current = false
    setDisplayedProgress(0)
    setIsCompletionSettled(false)
  }, [clearRampTimers])

  const runCompletionRamp = useCallback(() => {
    if (completionStartedRef.current) {
      return
    }

    completionStartedRef.current = true
    clearTimer(dummyTimerRef)

    const steps = buildCompletionRampSteps(displayedRef.current)

    steps.forEach((stepValue, index) => {
      const timerId = setTimeout(() => {
        setDisplayed(stepValue)

        if (index === steps.length - 1) {
          setIsCompletionSettled(true)
        }
      }, (index + 1) * 90)

      rampTimersRef.current.push(timerId)
    })

    if (steps.length === 0) {
      setDisplayed(100)
      setIsCompletionSettled(true)
    }
  }, [setDisplayed])

  const applyMergedProgress = useCallback(() => {
    const merged = mergeDisplayedProgress({
      previousDisplayed: displayedRef.current,
      dummyProgress: dummyRef.current,
      realProgress: realRef.current,
    })

    if (merged !== displayedRef.current) {
      setDisplayed(merged)
    }
  }, [setDisplayed])

  const scheduleDummyTick = useCallback(() => {
    clearTimer(dummyTimerRef)

    if (!isActive || backendCompletedRef.current) {
      return
    }

    const delay = getDummyDelayForProgress(dummyRef.current)
    if (!delay) {
      return
    }

    dummyTimerRef.current = setTimeout(() => {
      if (!isActive || backendCompletedRef.current) {
        return
      }

      if (dummyRef.current < 99) {
        dummyRef.current += 1
      }

      applyMergedProgress()
      scheduleDummyTick()
    }, delay)
  }, [applyMergedProgress, isActive])

  const consumeBackendProgress = useCallback(
    payload => {
      const normalized = normalizeBackendCourseProgress(payload)
      const nextReal = clampProgress(normalized.realProgress)
      realRef.current = Math.max(realRef.current, nextReal)
      dummyRef.current = Math.max(dummyRef.current, realRef.current)

      if (normalized.isComplete && !backendCompletedRef.current) {
        backendCompletedRef.current = true
        runCompletionRamp()
      } else if (!backendCompletedRef.current) {
        applyMergedProgress()
        if (!dummyTimerRef.current) {
          scheduleDummyTick()
        }
      }

      return normalized
    },
    [applyMergedProgress, runCompletionRamp, scheduleDummyTick],
  )

  useEffect(() => {
    if (isActive) {
      scheduleDummyTick()
      return
    }

    reset()
  }, [isActive, reset, scheduleDummyTick])

  useEffect(() => {
    return () => {
      clearTimer(dummyTimerRef)
      clearRampTimers()
    }
  }, [clearRampTimers])

  return {
    displayedProgress,
    isCompletionSettled,
    consumeBackendProgress,
    reset,
  }
}

export default useBlendedCourseProgress
